import { ReactElement, useEffect, useState } from "react"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons"

import { useParams } from "react-router-dom"
import { useDataModaUploadContext, IFormFieldList, ISchema } from "../../../providers/DataModaUploadContext"
import { useUpdateMSJsonSchemaMutation } from "../../../services/marketprices/mappingSchemas"
import { IModaFetchData } from "../../../interfaces/markets"
import { useSaveModaFetchMutation } from "../../../services/marketprices/commodityPrice"

const ModaFinalize = (): ReactElement => {
  const { sId } = useParams<{ sId: string }>()
  const { schemaId, modaFormFields, commonSchema, keyValue, modaForm, setIsFetchRequestCompleted } =
    useDataModaUploadContext()

  const [fetchMessage, setFetchMessage] = useState<string>("")
  const [apiError, setApiError] = useState<boolean>(false)

  const [updateJsonSchema, { isLoading, isSuccess }] = useUpdateMSJsonSchemaMutation()
  const [saveFetch] = useSaveModaFetchMutation()

  const handleSaveFetch = () => {
    const fetchData: IModaFetchData = {
      apiKey: keyValue as number,
      projectId: modaForm.projectId as number,
      formId: modaForm.formId as number,
      frequencyId: modaForm.frequencyId as number,
      launchAndForget: false,
      mappingSchemaId: schemaId as number,
      dataCollectionFrequencyId: modaForm.dataCollectionFrequencyId as number,
      endDate: modaForm.endDate as string,
      dataCollectionStartDate: modaForm.dataCollectionStartDate as string,
      dataCollectionEndDate: modaForm.dataCollectionEndDate as string,
    }

    saveFetch(fetchData)
      .unwrap()
      .then(() => {
        setFetchMessage(
          "The fetch is ongoing at that new reports will be generate automatically once new data are retrieved.",
        )
        setIsFetchRequestCompleted(true)
      })
      .catch(error => {
        setApiError(true)
        setIsFetchRequestCompleted(false)
        setFetchMessage(`Error saving fetch data:  ${error?.data?.message || error.message || "Unknown error"}`)
      })
  }

  const handleUpdateSchema = () => {
    setApiError(false)
    const updatedSchema: ISchema = { schema: [], common: {} }
    const selectedFields = modaFormFields.filter(field => field.assignedTo)
    const linearObject = {}

    // Select type of assignement
    const isLinearObject = (item: IFormFieldList) => {
      const commonTypes = ["date", "market"]
      if (commonTypes.includes(item?.assignedTo || "")) {
        return item.isRepeatable
      }
      return item?.assignedType?.length === 1
    }

    const assignType = (val?: string) => {
      if (val === "priceValue") {
        return "decimal"
      }
      return "string"
    }

    selectedFields.forEach(field => {
      if (isLinearObject(field)) {
        const type = field.assignedType?.[0] // assigned to here is never undefined. none is just for typescript
        linearObject[type || "none"] = {
          cellValue: field.fieldName,
          type: field.assignedTo === "date" ? "dateTime" : assignType(field?.assignedTo),
          parentType: field.isRepeatable ? "array" : undefined,
          parentName: field.isRepeatable ? field?.repeatableGroupName : undefined,
        }
      } else if (field?.assignedType && field.assignedType?.length > 1) {
        const assignValue = (ind: number): string | undefined => {
          if (ind === 0) {
            return undefined
          }
          if (ind === 1) {
            return field.assignedCommodity
          }
          return field.assignedUnit
        }
        const Obj = {}
        field.assignedType.forEach((type, i) => {
          Obj[type] = {
            cellValue: i === 0 ? field.fieldName : undefined,
            value: assignValue(i),
            type: assignType(field?.assignedTo),
            parentType: field.isRepeatable ? "array" : undefined,
            parentName: field.isRepeatable ? field?.repeatableGroupName : undefined,
          }
        })
        updatedSchema.schema.push(Obj)
      } else {
        updatedSchema.common[field?.assignedTo || "none"] = {
          cellValue: field.fieldName,
          type: field.assignedTo === "date" ? "dateTime" : assignType(field.assignedTo),
          parentType: field.isRepeatable ? "array" : undefined,
          parentName: field.isRepeatable ? field?.repeatableGroupName : undefined,
        }
      }
    })

    Object.keys(commonSchema).forEach(key => {
      if (!updatedSchema.common[key as keyof typeof updatedSchema.common] && commonSchema[key].value) {
        updatedSchema.common = {
          ...updatedSchema.common,
          [key]: { ...commonSchema[key as keyof typeof commonSchema] },
        }
      }
    })
    updatedSchema.schema.push(linearObject)
    const updateSchemaToString = JSON.stringify(updatedSchema, null, 0)
    updateJsonSchema({
      msId: schemaId as number,
      schema: updateSchemaToString,
    })
      .unwrap()
      .then(() => {
        if (sId) return
        handleSaveFetch() // Call to save fetch data after schema update
      })
      .catch(error => {
        setApiError(true)
        console.error("Error updating schema:", error)
      })
  }

  useEffect(() => {
    handleUpdateSchema()
  }, [])

  return (
    <div className="grid grid-cols-1 gap-x-2 gap-y-6 w-full max-w-[1000px] mx-auto items-center w-100">
      <div className="font-bold mb-3 p-4 w-100">
        <FontAwesomeIcon icon={faCheckCircle} className="mr-2" size="2xl" color="var(--support-02)" />
        {!sId ? (
          <>
            <span className="text-lg">Mapping saved</span>
            <div className="mt-2">
              <span className="text-orange-500 mt-2">{isLoading && `...updating schema`}</span>
              <span className={apiError ? "text-red-500 mt-2" : "mt-2"}>{isSuccess && fetchMessage}</span>
              <span className="text-red-500">{apiError && "Error Saving schema or fetch operation"}</span>
            </div>

            <div className="mt-4 text-justify">
              <p className="font-bold mb-2">MODA connection active</p>
              <p>
                New data will be fetched automatically according to your configured schedule. New reports will be
                generated automatically when new MODA data is retrieved. You can view them in the Reports page. Pending
                reports will also appear in the Dashboard.
              </p>
              <p className="mt-1">You can edit this mapping anytime from the Mapping section.</p>
            </div>
          </>
        ) : (
          <span>Updated Schema: ({`${schemaId}`})</span>
        )}
      </div>
    </div>
  )
}

export default ModaFinalize
